/* eslint-disable security/detect-object-injection */
import React, { useRef, ChangeEvent, useEffect, useMemo, useState } from 'react';
import {
    ColumnDef,
    ColumnFiltersState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable
} from '@tanstack/react-table';
import { CorrectProduct, CustomCellContext, SelectedProductsProps } from '../lxs-create-order.data';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react';
import { IPostCartItems } from '../../../actions/post-cart.action';
import { IEndCustomer, IGetAllProductsResult } from '../../../actions/get-all-products.action';
import {
    CREATE_ORDER_COLUMN_PRODUCT_NAME,
    CREATE_ORDER_COLUMN_PRODUCT_NUMBER,
    CREATE_ORDER_COLUMN_ACTION,
    CREATE_ORDER_COLUMN_END_CUSTOMER,
    CREATE_ORDER_COLUMN_CUSTOMER_PRODUCT_NUMBER,
    CREATE_ORDER_COLUMN_REQUESTED_DELIVERY_DATE,
    NO_DATA_FALLBACK,
    CREATE_ORDER_COLUMN_INCOTERM,
    CREATE_ORDER_COLUMN_PRICE
} from '../../../configuration';
import toaster from './toast';
import ProductEndcustomerTableCellRef from './selected-products-endcustomer-cell';
import ProductQuantityTableCellRef from './selected-products-quantity-cell';
import { useModal } from '../../../components/modal';
import PriceInformationModal from './price-information-popup';
import { replacePlaceholders } from '../../../utilities/replace-placeholder-in-translation';
import { getConversionFactor } from '../../../utilities';
import { formatPriceIndication } from '../../../utilities/format-price-indication';
import Tooltip from '../../../components/tooltip';

export default function SelectedProducts({
    resources,
    orderData,
    productsData,
    productsHaveEndcustomers,
    globalDate,
    headerColumnFilters,
    fetchPrices,
    goto,
    setProducts,
    setGlobalDate
}: SelectedProductsProps) {
    const tomorrow = new Date(+new Date() + 86400000);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [applyGlobalDate, setApplyGlobalDate] = useState<Boolean>(false);
    const [updateTable, setUpdateTable] = useState<Boolean>(false);
    const [updateProds, setUpdateProds] = useState<Boolean>(false);
    const inputRefs = useRef<HTMLInputElement[]>([]);
    const toastId = 'quantityChangeToast';
    const className = 'lxs-create-order';

    const { setOpen } = useModal();

    // Autoselect the added item
    useEffect(() => {
        if (orderData.products?.length) {
            const lastRef = inputRefs.current[orderData.products?.length - 1];
            lastRef && lastRef.focus();
        }
    }, [orderData.products?.length]);

    const checkForDecimal = (input: number): boolean => input % 1 !== 0;

    const showToast = (heading: string, message: string, type?: string) => {
        toaster({ heading, message, toastId, type });
    };

    const updateProductQuantity = (
        newValue: string,
        customProps: CustomCellContext<IPostCartItems, unknown>,
        currentProductIndex: number
    ) => {
        const updatedProducts = [...customProps.products];
        updatedProducts[currentProductIndex].Quantity = newValue;
        customProps.setProducts(updatedProducts);
    };

    const calculatePackageValue = (baseValue: number, packagingQty: number) => {
        if (packagingQty === 0) {
            return baseValue;
        }
        const packageValue = (baseValue / packagingQty) * packagingQty;
        return Number(packageValue.toFixed(3));
    };

    const updateSelectedOrderProduct = (product: IPostCartItems, correctProduct: CorrectProduct) => {
        product.EndCustomerName = correctProduct?.EndCustomerDisplayName || '';
        product.EndCustomerNumber = correctProduct?.EndCustomerNumber || '';
        product.PriceAmount = correctProduct?.PriceAmount || '';
        product.PriceCurrency = correctProduct?.PriceCurrency || '';
        product.PriceFactor = correctProduct?.PriceFactor || '';
        product.PriceUnit = correctProduct?.PriceUnit || '';
        product.MinimumQty = correctProduct?.MinimumQty || '';
        product.MinimumQtyUnit = correctProduct?.MinimumQtyUnit || '';
        product.PackagingQty = correctProduct?.PackagingQty || '';
        product.MinimumQtyOnOrderLevel = correctProduct?.MinimumQtyOnOrderLevel ?? false;
    };

    const handleEndCustomerChange = (
        customProps: CustomCellContext<IPostCartItems, unknown>,
        relevantProducts: IGetAllProductsResult | undefined,
        event: ChangeEvent<HTMLSelectElement>,
        currentProductIndex: number
    ) => {
        const orderProducts = customProps.products.slice();
        const correctProduct = relevantProducts?.EndCustomers.find(
            (endcustomer: IEndCustomer) => endcustomer.EndCustomerNumber === event.target.value
        );
        const selectedOrderProduct = orderProducts[currentProductIndex];
        if (correctProduct) {
            updateSelectedOrderProduct(selectedOrderProduct, correctProduct);
            const { Quantity } = orderProducts[Number(currentProductIndex)];
            const { MinimumQty, PackagingQty } = selectedOrderProduct;
            const minimumQtyNumber = MinimumQty?.replace(/,/g, '.');
            const packagingQtyNumber = PackagingQty?.replace(/,/g, '.');
            const quantityToUpdate =
                correctProduct.MinimumQtyOnOrderLevel === false && Number(Quantity) < Number(minimumQtyNumber)
                    ? minimumQtyNumber
                    : Quantity;
            handleQuantityRoundup(quantityToUpdate!, packagingQtyNumber, customProps, currentProductIndex);

            if (correctProduct.MinimumQtyOnOrderLevel !== false && Number(Quantity) < Number(minimumQtyNumber)) {
                setUpdateTable(!updateTable);
            }
        }
        customProps.setProducts(orderProducts);
    };

    const handleQuantityMinimum = (
        input: string,
        minQty: string,
        setValue: (qtyInput: string) => void,
        setDisplayValue: (qtyInput: string) => void,
        customProps: CustomCellContext<IPostCartItems, string>,
        currentProductIndex: number,
        packagingQty: string
    ) => {
        if (Number(input) < Number(minQty)) {
            setValue(minQty);

            setDisplayValue(minQty);
            updateProductQuantity(minQty, customProps, currentProductIndex);
            if (packagingQty) {
                handleQuantityRoundup(minQty, packagingQty, customProps, currentProductIndex, setValue, setDisplayValue, true);
            }
        } else {
            if (input === '') {
                updateProductQuantity('', customProps, currentProductIndex);
                return;
            }
            handleQuantityRoundup(input, packagingQty, customProps, currentProductIndex, setValue, setDisplayValue);
        }
    };

    const handleQuantityRoundup = (
        input: string | number,
        PackagingQty: string | undefined,
        customProps: CustomCellContext<IPostCartItems, string | unknown>,
        currentProductIndex: number,
        setValue?: (qtyInput: string) => void,
        setDisplayValue?: (qtyInput: string) => void,
        showToastMessage?: boolean
    ) => {
        const initialQty = typeof input === 'string' ? Number(input.replace(',', '.')) : input;
        const roundedInitialQty = Number(initialQty.toPrecision(12)); // Round to 12 decimal places to prevent floating-point precision errors

        if (roundedInitialQty >= 0 && PackagingQty) {
            const packagingQtyNum = Number(PackagingQty);
            if (initialQty % packagingQtyNum !== 0) {
                const qtyInput = calculatePackageValue(roundedInitialQty, packagingQtyNum);
                let roundedQtyInput = Number(qtyInput).toString();
                roundedQtyInput = parseFloat(roundedQtyInput).toString(); // Remove trailing zeros
                setValue?.(roundedQtyInput);

                setDisplayValue?.(checkForDecimal(Number(roundedQtyInput)) ? Number(roundedQtyInput).toFixed(3) : roundedQtyInput);
                updateProductQuantity(roundedQtyInput, customProps, currentProductIndex);
                if (Number(roundedQtyInput) !== roundedInitialQty || showToastMessage) {
                    showToast(
                        resources.createOrderSelectedProductsQuantityUpdateToastTitle,
                        resources.createOrderSelectedProductsQuantityUpdateToastMessage
                    );
                }
            } else {
                updateProductQuantity(roundedInitialQty.toString(), customProps, currentProductIndex);
            }
        }
    };

    const handleQuantityChange = (
        event: ChangeEvent<HTMLInputElement>,
        setValue: (qtyInput: string) => void,
        setDisplayValue: (qtyInput: string) => void
    ) => {
        const target = event.target as HTMLInputElement;
        let value = target.value;

        // Remove any characters that are not digits or a single dot
        value = value.replace(/[^0-9.]/g, '');

        // Ensure only one dot is allowed
        const dotIndex = value.indexOf('.');
        if (dotIndex !== -1) {
            value = value.slice(0, dotIndex + 1) + value.slice(dotIndex + 1).replace(/\./g, '');
        }

        // Limit to three decimal places
        const decimalIndex = value.indexOf('.');
        if (decimalIndex !== -1 && value.length - decimalIndex - 1 > 3) {
            value = value.substring(0, decimalIndex + 4);
        }

        setValue(value);
        setDisplayValue(value);
    };

    const calculateMinOrderQuantities = (
        products: IPostCartItems[],
        headerColumnFilters: ColumnFiltersState,
        productsData: IGetAllProductsResult[]
    ) => {
        let totalQuantity = 0;
        let highestMinimumQty = 0;
        let unit = '';
        let productNumber;
        let productUnit;
        let isInvalid: 'zero-value' | 'unit-error' | '';
        isInvalid = '';

        products?.forEach(orderProduct => {
            const columnFilterIncoterm = headerColumnFilters.find((filter: { id: string }) => filter.id === CREATE_ORDER_COLUMN_INCOTERM)
                ?.value as string;
            const quantity = Number(orderProduct.Quantity.replace(/,/g, '.'));
            if (quantity === 0) {
                isInvalid = 'zero-value';
            }

            const foundProd = productsData?.find(
                product => product.ProductNumber === orderProduct.ProductNumber && product.Incoterm1 === columnFilterIncoterm
            );
            if (foundProd) {
                const search = foundProd?.EndCustomers.find(e => e.EndCustomerNumber === orderProduct.EndCustomerNumber);

                if (search?.MinimumQtyOnOrderLevel) {
                    const minimumQty = Number(search.MinimumQty.replace(/,/g, '.'));
                    // Convert back to base unit
                    const baseUnitConversionFactor = getConversionFactor(foundProd, orderProduct.UnitOfMeasure, false);
                    const quantityInBaseUnit = quantity * baseUnitConversionFactor;
                    // Convert in MinimumQtyUnit
                    const minQtyUnitFactor = getConversionFactor(foundProd, search.MinimumQtyUnit, true);
                    const convertedMinQuantity = quantityInBaseUnit * minQtyUnitFactor;

                    if (unit !== '' && unit !== search.MinimumQtyUnit) {
                        isInvalid = 'unit-error';
                    }

                    totalQuantity += Number(convertedMinQuantity.toPrecision(12));

                    if (minimumQty > highestMinimumQty) {
                        highestMinimumQty = minimumQty;
                        unit = search.MinimumQtyUnit;
                        productNumber = foundProd?.ProductNumber;
                        productUnit = search.MinimumQtyUnit;
                    }
                }
            }
        });

        return { totalQuantity, highestMinimumQty, productNumber, productUnit, isInvalid };
    };

    const duplicateItemRow = (product: IPostCartItems) => {
        if (orderData.products?.length !== undefined && product) {
            // deep clone the product to be able to modify its values
            const copyItem = JSON.parse(JSON.stringify(product));
            setProducts([...orderData.products, copyItem]);
        }
    };

    const columns: ColumnDef<IPostCartItems>[] = useMemo(
        () => [
            {
                accessorFn: row => row.ProductName,
                id: CREATE_ORDER_COLUMN_PRODUCT_NAME,
                cell: info => info.getValue() ?? NO_DATA_FALLBACK,
                header: () => resources.createOrderProductsTableProductName,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.ProductNumber,
                id: CREATE_ORDER_COLUMN_PRODUCT_NUMBER,
                cell: info => info.getValue() ?? NO_DATA_FALLBACK,
                header: () => resources.createOrderProductsTableProductNumber,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.CustomerProductNumber,
                id: CREATE_ORDER_COLUMN_CUSTOMER_PRODUCT_NUMBER,
                cell: info => info.getValue() ?? NO_DATA_FALLBACK,
                header: () => resources.createOrderSelectedProductsTableCustomerProductNumber,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.RequestedDate,
                id: CREATE_ORDER_COLUMN_REQUESTED_DELIVERY_DATE,
                cell: props => {
                    const customProps = props as CustomCellContext<IPostCartItems, Date>;
                    const currentProductIndex = customProps.row.index;
                    const currentDate = new Date(customProps.products[Number(currentProductIndex)].RequestedDate);
                    const validDate = new Date(currentDate).getTime() > new Date(tomorrow).getTime() ? currentDate : tomorrow;

                    useEffect(() => {
                        const newProducts = customProps.products.slice();
                        newProducts[Number(currentProductIndex)].RequestedDate = validDate;
                        customProps.setProducts(newProducts);
                    }, [globalDate]);

                    return (
                        <fieldset className='msc-form__input-group mb-0'>
                            <div className='msc-form__input-group-container'>
                                <input
                                    id={`requestedDeliveryDate-${currentProductIndex}`}
                                    type='date'
                                    onKeyDown={e => {
                                        e.key === 'Enter' && e.preventDefault();
                                    }}
                                    placeholder='dd.mm.yyyy'
                                    min={tomorrow.toISOString().substring(0, 10)}
                                    value={(validDate && validDate?.toISOString().substring(0, 10)) ?? undefined}
                                    onChange={date => {
                                        const newProducts = customProps.products.slice();
                                        newProducts[Number(currentProductIndex)].RequestedDate = new Date(date.target.value);
                                        customProps.setProducts(newProducts);
                                    }}
                                />
                            </div>
                        </fieldset>
                    );
                },
                header: () => resources.createOrderSelectedProductsTableRequestedDeliveryDate,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.Quantity,
                id: 'quantity',
                cell: props => {
                    return (
                        <ProductQuantityTableCellRef
                            props={props}
                            headerColumnFilters={headerColumnFilters}
                            productsData={productsData}
                            calculatePackageValue={calculatePackageValue}
                            updateProductQuantity={updateProductQuantity}
                            inputRefs={inputRefs}
                            handleQuantityChange={handleQuantityChange}
                            handleQuantityMinimum={handleQuantityMinimum}
                            handleQuantityRoundup={handleQuantityRoundup}
                            resources={resources}
                        />
                    );
                },
                header: () => resources.createOrderSelectedProductsTableQuantity,
                footer: props => props.column.id
            },
            {
                id: CREATE_ORDER_COLUMN_PRICE,
                cell: props => {
                    const product = props.row.original;
                    const innerProps = props as any;

                    if (innerProps.fetchingPrices === true) {
                        return <Spinner className={`${className}__loading`} size={SpinnerSize.large} />;
                    }

                    if (product.Quantity === '' || product.EndCustomerNumber === '' || product.MissingParameters) {
                        return <></>;
                    }

                    return (
                        <a
                            className={`${className}__price-indication`}
                            onClick={() =>
                                // Instant load will cause the modal to load an empty body
                                setTimeout(() => {
                                    setOpen({
                                        content: (
                                            <PriceInformationModal
                                                props={props}
                                                resources={resources}
                                                productsData={productsData?.at(props.row.index)}
                                                orderData={orderData}
                                            />
                                        ),
                                        header: resources.additionalPriceInformationPopupHeadline,
                                        size: 'lg'
                                    });
                                }, 200)
                            }
                            title={resources.additionalPriceInformationPopupHeadline}
                        >
                            {product.PriceMissing
                                ? resources.createOrderProductsTablePriceIndicationNotAvailableMessage
                                : formatPriceIndication(product)}
                        </a>
                    );
                },
                header: () => (
                    <Tooltip
                        label={resources.createOrderProductsTablePriceTitle}
                        content={resources.createOrderProductsTablePriceInformation}
                    />
                ),
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.EndCustomerName,
                id: CREATE_ORDER_COLUMN_END_CUSTOMER,
                cell: props => {
                    return (
                        <ProductEndcustomerTableCellRef
                            props={props}
                            headerColumnFilters={headerColumnFilters}
                            productsData={productsData}
                            handleEndCustomerChange={handleEndCustomerChange}
                            resources={resources}
                        />
                    );
                },
                header: () => resources.createOrderSelectedProductsTableEndCustomer,
                footer: props => props.column.id
            },
            {
                id: CREATE_ORDER_COLUMN_ACTION,
                cell: info => info.getValue() ?? NO_DATA_FALLBACK,
                header: () => resources.createOrderProductsTableActionTitle,
                footer: props => props.column.id
            }
        ],
        [headerColumnFilters, applyGlobalDate, updateTable, updateProds]
    );

    const [columnVisibility] = React.useState<VisibilityState>({
        [CREATE_ORDER_COLUMN_END_CUSTOMER]: productsHaveEndcustomers ?? true
    });

    const table = useReactTable({
        data: orderData.products ?? [],
        columns,
        filterFns: {},
        state: {
            columnFilters,
            columnVisibility
        },
        onColumnFiltersChange: setColumnFilters,
        globalFilterFn: 'includesString',
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        debugTable: false,
        debugHeaders: false,
        debugColumns: false
    });

    return (
        <>
            <div className='row'>
                <div className='col-12'>
                    <div className={`${className}__selected-products-heading-container`}>
                        <h3 className={`${className}__total-entries`}>{resources.createOrderSelectedProductsHeading}</h3>
                        <a className={`${className}__remove-button`} onClick={() => setProducts([])}>
                            {resources.createOrderSelectedProductsRemoveAll}
                        </a>
                        <div className={`${className}__global-date`}>
                            <fieldset className='msc-form__input-group lxs-create-order__global-date-input'>
                                <div className='msc-form__input-group-container'>
                                    <input
                                        id={`requestedDeliveryDate`}
                                        type='date'
                                        onKeyDown={e => {
                                            e.key === 'Enter' && e.preventDefault();
                                        }}
                                        placeholder='dd.mm.yyyy'
                                        min={tomorrow.toISOString().substring(0, 10)}
                                        value={(globalDate && globalDate?.toISOString().substring(0, 10)) ?? undefined}
                                        onChange={date => {
                                            const currentDate = new Date(date.target.value);
                                            const validDate = currentDate.getTime() > new Date(tomorrow).getTime() ? currentDate : tomorrow;
                                            setGlobalDate(validDate);
                                        }}
                                    />
                                </div>
                            </fieldset>
                            <button
                                disabled={!orderData?.products || orderData?.products?.length === 0}
                                className={`msc-cta__primary ${className}__global-date-btn`}
                                onClick={() => {
                                    if (globalDate !== undefined) {
                                        const newProducts = orderData.products?.slice();
                                        newProducts?.map(product => {
                                            product.RequestedDate = globalDate;
                                        });
                                        newProducts && setProducts(newProducts);
                                        setApplyGlobalDate(true);
                                    }
                                }}
                            >
                                {resources.createOrderSelectedProductsDateForAll}
                            </button>
                        </div>
                    </div>
                    <div className='table-responsive'>
                        <form
                            id='selected-products'
                            onSubmit={event => {
                                event.preventDefault();
                                if (orderData.products && productsData) {
                                    const {
                                        totalQuantity,
                                        highestMinimumQty,
                                        productNumber,
                                        productUnit,
                                        isInvalid
                                    } = calculateMinOrderQuantities(orderData.products, headerColumnFilters, productsData);

                                    if ((totalQuantity < highestMinimumQty && productNumber && productUnit) || isInvalid) {
                                        if (isInvalid && isInvalid !== '') {
                                            switch (isInvalid) {
                                                case 'unit-error': {
                                                    showToast(
                                                        resources.createOrderProductsOrderLevelQuantityUpdateToastTitle,
                                                        resources.createOrderErrorCreateOrderUnitHint,
                                                        'error'
                                                    );
                                                    break;
                                                }
                                                case 'zero-value': {
                                                    showToast(
                                                        resources.createOrderProductsOrderLevelQuantityUpdateToastTitle,
                                                        resources.createOrderErrorCreateOrderZeroHint,
                                                        'error'
                                                    );
                                                    break;
                                                }
                                                default: {
                                                    break;
                                                }
                                            }
                                        } else {
                                            const message = replacePlaceholders(
                                                resources.createOrderProductsOrderLevelQuantityUpdateToastMessage,
                                                {
                                                    productNumber: productNumber!,
                                                    minimumQty: highestMinimumQty.toString(),
                                                    unit: productUnit!
                                                }
                                            );
                                            showToast(resources.createOrderProductsOrderLevelQuantityUpdateToastTitle, message, 'error');
                                        }
                                    } else {
                                        goto(4);
                                    }
                                }
                            }}
                        >
                            <table className='table'>
                                <thead>
                                    {table.getHeaderGroups().map(headerGroup => (
                                        <tr key={headerGroup.id}>
                                            {headerGroup.headers.map(header => {
                                                return (
                                                    <th key={header.id} colSpan={header.colSpan}>
                                                        {header.isPlaceholder ? null : (
                                                            <>{flexRender(header.column.columnDef.header, header.getContext())}</>
                                                        )}
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {table.getRowModel().rows.map(row => (
                                        <React.Fragment key={row.id}>
                                            <tr>
                                                {row.getVisibleCells().map(cell => {
                                                    return cell.column?.id === CREATE_ORDER_COLUMN_ACTION ? (
                                                        <td className={`${className}__selected-products-action`}>
                                                            <a
                                                                className={`${className}__copy-button`}
                                                                title={resources.createOrderSelectedProductsTableCopyLabel}
                                                                onClick={() => {
                                                                    duplicateItemRow(cell.row.original);
                                                                }}
                                                            ></a>
                                                            <a
                                                                className={`${className}__remove-button`}
                                                                title={resources.createOrderSelectedProductsTableRemoveLabel}
                                                                onClick={() => {
                                                                    const newSelectedProducts = orderData.products
                                                                        ? orderData.products.slice()
                                                                        : [];
                                                                    newSelectedProducts.splice(Number(cell.row.id), 1);
                                                                    setProducts(newSelectedProducts);
                                                                    setUpdateProds(!updateProds);
                                                                }}
                                                            ></a>
                                                        </td>
                                                    ) : (
                                                        <td key={cell.id}>
                                                            {flexRender(cell.column.columnDef.cell, {
                                                                ...cell.getContext(),
                                                                products: orderData.products,
                                                                setProducts: setProducts,
                                                                fetchingPrices: fetchPrices
                                                            })}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
}
